import { isNil } from 'lodash';
import React, { useCallback, useMemo } from 'react';

import { TrendingDown, TrendingUp } from '@mui/icons-material';

import BaseScore from 'pages/Bulletin/components/BaseScore';
import { ChartData } from 'pages/Bulletin/types/chart-data';
import { HistoryOfChangesItem } from 'pages/Bulletin/types/history-of-changes';
import { isProtectedData } from 'pages/components/ProtectedData';

import useStyles from './styles';

interface Props {
    diagram?: boolean;
    aiScore: {
        value: number;
        uncertanity: number;
    };
}

const AI: React.FC<Props> = ({ aiScore, diagram }) => {
    const { classes, cx } = useStyles();
    const { value, uncertanity } = aiScore;

    const statistics = useCallback((data: HistoryOfChangesItem[]) => {
        if (data.every(({ changes }) => 'score' in changes === false)) {
            return null;
        }

        return data.reduce<ChartData>((output, { version, published, changes }) => {
            const score = changes.score?.value ?? output.at(-1)?.score ?? 0;
            output.push({ version, score, published });
            return output;
        }, []);
    }, []);

    const isHighConfidence = uncertanity <= 1.3;

    const data = useMemo(
        () => [
            {
                score: value,
                statistics,
                parts: {
                    confidence: isNil(uncertanity) ? (
                        '-'
                    ) : isProtectedData(uncertanity) ? (
                        uncertanity
                    ) : (
                        <span
                            className={cx(classes.confidence, {
                                [classes.high]: isHighConfidence,
                                [classes.low]: !isHighConfidence
                            })}
                        >
                            {isHighConfidence ? <TrendingUp /> : <TrendingDown />}
                            {isHighConfidence ? 'High' : 'Low'}
                        </span>
                    )
                }
            }
        ],
        [classes.confidence, classes.high, classes.low, cx, isHighConfidence, statistics, uncertanity, value]
    );

    if (!value) {
        return null;
    }

    return <BaseScore diagram={diagram} label="Vulners AI Score" data={data} />;
};

export default AI;
